import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { STATUS } from "../../common/Const";
import { hasErrors, hasNoErrors } from "../../common/Helpers";
import { history } from "../../../redux/store/configureStore";
import * as clientActions from "../../../redux/actions/clientActions";
import * as enrollmentActions from "../../../redux/actions/enrollmentActions";
import * as productActions from "../../../redux/actions/productActions";
import * as studentActions from "../../../redux/actions/studentActions";
import {
  PARTICIPATION_STRATEGIES,
  SCHEDULE_ITEM_SOURCE_TYPES,
} from "../../billingEnrollment/billingEnrollmentTypes";
import {
  buildPaymentScheduleItems,
  validateEnrollment,
} from "../../billingEnrollment/billingEnrollmentValidation";
import { buildManageRouteWithSchoolYear, getNormalizedSchoolYearStartYearFromSearch } from "../manageSchoolYearRouting";
import EnrollmentForm from "./EnrollmentForm";

const todayDate = () => new Date().toISOString().slice(0, 10);
const getProductById = (products, id) => products.find(product => product.id === id) || null;
const labelForPerson = item => `${item.firstName || ""} ${item.lastName || ""}`.trim();

function buildSchedulePreview(enrollment, product) {
  if (!product) return enrollment.paymentScheduleItems || [];

  const dueDate = enrollment.firstPaymentDueDate || enrollment.startDate || todayDate();
  const previewItems = [];
  const initialFee = Number(enrollment.initialFee || 0);
  const materialsFee = Number(enrollment.materialsFee || 0);
  const defaultPrice = Number(product.defaultPrice || 0);

  if (initialFee > 0) {
    previewItems.push({
      dueDate,
      amount: initialFee,
      sourceType: SCHEDULE_ITEM_SOURCE_TYPES.INITIAL_FEE,
      description: "Oplata poczatkowa",
    });
  }
  if (materialsFee > 0) {
    previewItems.push({
      dueDate,
      amount: materialsFee,
      sourceType: SCHEDULE_ITEM_SOURCE_TYPES.MATERIALS_FEE,
      description: "Materialy",
    });
  }
  if (defaultPrice > 0) {
    previewItems.push({
      dueDate,
      amount: defaultPrice,
      sourceType: product.paymentStrategy === "prepaid"
        ? SCHEDULE_ITEM_SOURCE_TYPES.PREPAID_PACKAGE
        : SCHEDULE_ITEM_SOURCE_TYPES.INSTALLMENT,
      description: product.paymentStrategy === "installments" ? "Rata 1" : "Oplata za produkt",
    });
  }

  return buildPaymentScheduleItems({
    enrollmentId: enrollment.id || "new-enrollment",
    items: previewItems,
  });
}

function decorateEnrollment(enrollment, clients, products) {
  const product = getProductById(products, enrollment.productId) || enrollment.product || null;
  const client = clients.find(item => item.id === enrollment.clientId);

  return {
    ...enrollment,
    product: product || enrollment.product,
    productName: product ? product.name : enrollment.productName,
    clientDisplayName: client ? labelForPerson(client) : enrollment.clientDisplayName,
    paymentScheduleItems: buildSchedulePreview(enrollment, product),
  };
}

function Enrollment({
  enrollment: initialEnrollment,
  enrollments,
  clients,
  students,
  products,
  loadClients,
  loadStudents,
  loadProducts,
  loadEnrollments,
  saveEnrollment,
}) {
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [enrollment, setEnrollment] = useState(decorateEnrollment(initialEnrollment, clients, products));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(STATUS.IDLE);

  useEffect(() => {
    if (clients.length === 0) loadClients().catch(error => alert(`Loading clients failed ${error}`));
    if (students.length === 0) loadStudents().catch(error => alert(`Loading students failed ${error}`));
    if (products.length === 0) loadProducts().catch(error => alert(`Loading products failed ${error}`));
    if (enrollments.length === 0) loadEnrollments().catch(error => alert(`Loading enrollments failed ${error}`));
  }, [
    clients.length,
    enrollments.length,
    loadClients,
    loadEnrollments,
    loadProducts,
    loadStudents,
    products.length,
    students.length,
  ]);

  useEffect(() => {
    setEnrollment(decorateEnrollment(initialEnrollment, clients, products));
  }, [clients, initialEnrollment, products]);

  const validate = (nextEnrollment = enrollment) => {
    const nextErrors = validateEnrollment(nextEnrollment);
    setErrors(nextErrors);
    return hasNoErrors(nextErrors);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const selectedValues = event.target.selectedOptions
      ? Array.from(event.target.selectedOptions).map(option => option.value)
      : [];
    const product = name === "productId" ? getProductById(products, value) : getProductById(products, enrollment.productId);
    const numericFields = ["initialFee", "materialsFee"];
    let nextEnrollment = {
      ...enrollment,
      [name]: numericFields.includes(name) && value !== "" ? Number(value) : value,
    };

    if (name === "studentIds") {
      nextEnrollment = { ...nextEnrollment, studentIds: value ? [value] : [] };
    }

    if (name === "eligibleStudentIds") {
      nextEnrollment = {
        ...nextEnrollment,
        eligibleStudentIds: selectedValues,
        studentIds: [],
      };
    }

    if (name === "productId") {
      nextEnrollment = {
        ...nextEnrollment,
        productId: value,
        product,
      };
    }

    nextEnrollment = decorateEnrollment(nextEnrollment, clients, products);
    setEnrollment(nextEnrollment);
    validate(nextEnrollment);
  };

  const handleCancel = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/enrollments", "", activeSchoolYearStartYear));
  };

  const buildSavePayload = () => {
    const { product } = enrollment;
    const payload = {
      ...enrollment,
      status: enrollment.status || "draft",
      termsSnapshot: product,
    };

    if (product && product.participationStrategy === PARTICIPATION_STRATEGIES.SHARED_STUDENTS) {
      return {
        ...payload,
        eligibleStudentIds: (enrollment.studentIds && enrollment.studentIds.length > 0)
          ? enrollment.studentIds
          : enrollment.eligibleStudentIds || [],
        studentIds: [],
      };
    }

    return {
      ...payload,
      eligibleStudentIds: [],
    };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    const payload = buildSavePayload();

    if (!validate(payload)) {
      setStatus(STATUS.SUBMITTED);
      return;
    }

    saveEnrollment(payload)
      .then(() => {
        setStatus(STATUS.COMPLETED);
        handleCancel();
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  return (
    <div className="mb-5">
      {hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => <div key={key}>{errors[key]}</div>)}
        </div>
      )}
      <EnrollmentForm
        clients={clients}
        students={students}
        products={products}
        enrollment={enrollment}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        status={status}
      />
    </div>
  );
}

Enrollment.propTypes = {
  enrollment: PropTypes.object.isRequired,
  enrollments: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadProducts: PropTypes.func.isRequired,
  loadEnrollments: PropTypes.func.isRequired,
  saveEnrollment: PropTypes.func.isRequired,
};

const newEnrollment = {
  status: "draft",
  startDate: todayDate(),
  studentIds: [],
  paymentScheduleItems: [],
};
const getEnrollmentById = (enrollments, id) => enrollments.find(enrollment => enrollment.id === id) || newEnrollment;

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  const enrollment = id === "new" || state.enrollments.length === 0 ? newEnrollment : getEnrollmentById(state.enrollments, id);

  return {
    enrollment,
    enrollments: state.enrollments,
    clients: state.clients.filter(client => !client.deleted),
    students: state.students.filter(student => !student.deleted),
    products: state.products.filter(product => !product.deleted),
  };
};

const mapDispatchToProps = dispatch => ({
  loadClients: () => dispatch(clientActions.loadClients()),
  loadStudents: () => dispatch(studentActions.loadStudents()),
  loadProducts: () => dispatch(productActions.loadProducts()),
  loadEnrollments: () => dispatch(enrollmentActions.loadEnrollments()),
  saveEnrollment: enrollment => dispatch(enrollmentActions.saveEnrollment(enrollment)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Enrollment);
